/**
 * Contains cookie related methods.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class CookieUtility {
  /**
   * Sets a cookie.
   *
   * @param {string} name Cookie name
   * @param {string} value Value
   * @param {Date} [domain] Domain name; if omitted, the current URL will be used without a leading dot (defaults to
   * nothing)
   * @param {Date} [expires] When the cookie expires (defaults to now + 1 year)
   * @param {string} [path] Path of the cookie (defaults to "/")
   * @param {string} [sameSite] SameSite attribute (defaults to "lax")
   * @returns {string} The complete cookie string
   */
  static set(
    name: string,
    value: string,
    domain: string = '',
    expires: Date | null = null,
    path: string = '/',
    sameSite: string = 'lax'
  ): string {
    let cookie = name + '=' + value + ';';
    if (expires === null) {
      expires = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
      cookie += 'expires=' + expires.toUTCString() + ';';
    }
    if (domain) {
      cookie += `domain=${domain};`;
    }
    cookie += `path=${path};SameSite=${sameSite}`;
    document.cookie = cookie;
    return cookie;
  }

  /**
   * Gets a cookie.
   *
   * @param {string} name Cookie name
   * @returns {string|null} Cookie value, if existing
   */
  static get(name: string): string | null {
    if (!CookieUtility.exists(name)) {
      return null;
    }
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const parts = cookie.split('=');
      let identifier = parts.shift();
      if (identifier) {
        identifier = identifier.trim();
        const value = parts.join('=').trim();
        if (identifier === name) {
          return value;
        }
      }
    }
    return null;
  }

  /**
   * Deletes a cookie.
   *
   * @param {string} name Cookie name
   * @param {Date} [domain] Domain name (defaults to nothing)
   * @param {string} [path] Path of the cookie (defaults to "/")
   * @param {string} [sameSite] SameSite attribute (defaults to "lax")
   * @returns {boolean} True when the cookie was removed, otherwise false
   */
  static delete(name: string, domain: string = '', path: string = '/', sameSite: string = 'lax'): boolean {
    if (!CookieUtility.exists(name)) {
      return false;
    }
    let cookie = name + `=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=${path};SameSite=${sameSite}`;
    if (domain) {
      cookie += `;domain=${domain}`;
    }
    document.cookie = cookie;
    return true;
  }

  /**
   * Checks whether a cookie exists or not.
   *
   * @param {string} name Name of the cookie to check
   * @returns {boolean} True when the cookie exists, otherwise false
   */
  static exists(name: string): boolean {
    if (!name) {
      return false;
    }
    return document.cookie.indexOf(name + '=') >= 0;
  }
}
